import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Grid,
  Paper,
  Typography,
  Collapse,
  Button,
  InputAdornment,
} from '@material-ui/core';
import { Formik, FormikProps } from 'formik';

import {
  Network,
  useRoiEngagementsOverviewStatsQuery,
  useRoiReachOverviewStatsQuery,
  useRoiClicksOverviewStatsQuery,
  useGetUserRoiValuesQuery,
  useUpdateUserRoiValuesMutation,
  RoiCurrencyValues,
} from 'api';
import { DatePickers, ValidatedTextFieldNext } from 'components';

import { ILineChartConfig, ROILineChart, TabbedChartLoader, Change } from '../shared';
import useStyles from '../analyticsStyles';

import { formatStat } from 'helpers';
import { useDateRange, useCurrentUser, useProtectedClient, useNotify } from 'hooks';

import { ROICurrencyInputSchema } from './validations';

const CHART_HEIGHT = 200;

enum ROIChartType {
  Reach = 'reach',
  Clicks = 'clicks',
  Engagements = 'engagements',
}

const DEFAULT_CURRENCY_VALUES: RoiCurrencyValues = {
  reach_twitter: 3.0,
  reach_linkedin: 13.75,
  clicks_twitter: 1.35,
  clicks_linkedin: 5.26,
  clicks_facebook: 1.07,
  engagements_twitter: 1.47,
  engagements_linkedin: 1.8,
  engagements_facebook: 3.15,
};

const ROIOverviewStats: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const user = useCurrentUser();
  const client = useProtectedClient();
  const notify = useNotify();

  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } = useDateRange();

  const [collapseValues, setCollapseValues] = useState(false);

  const userROIValuesQueryResult = useGetUserRoiValuesQuery(
    {
      user_id: user.id,
    },
    {
      onError: notify.mutationError,
      onSuccess: data => {
        updateCurrencyValues(data.getUserROIValues);
      },
    }
  );

  const [currencyValues, setCurrencyValues] = useState(
    userROIValuesQueryResult?.data?.getUserROIValues || DEFAULT_CURRENCY_VALUES
  );

  const { mutate } = useUpdateUserRoiValuesMutation({
    onError: notify.mutationError,
  });

  const engagementsQueryResult = useRoiEngagementsOverviewStatsQuery(
    {
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      twitter_currency_value: currencyValues.engagements_twitter,
      linkedin_currency_value: currencyValues.engagements_linkedin,
      facebook_currency_value: currencyValues.engagements_facebook,
    },
    {
      onError: notify.queryError,
      enabled: !!userROIValuesQueryResult.data,
    }
  );

  const clicksQueryResult = useRoiClicksOverviewStatsQuery(
    {
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      twitter_currency_value: currencyValues.clicks_twitter,
      linkedin_currency_value: currencyValues.clicks_linkedin,
      facebook_currency_value: currencyValues.clicks_facebook,
    },
    {
      onError: notify.queryError,
      enabled: !!userROIValuesQueryResult.data,
    }
  );

  const reachQueryResult = useRoiReachOverviewStatsQuery(
    {
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      twitter_currency_value: currencyValues.reach_twitter,
      linkedin_currency_value: currencyValues.reach_linkedin,
    },
    {
      onError: notify.queryError,
      enabled: !!userROIValuesQueryResult.data,
    }
  );

  const getROIQueryLoadingStatus = (roiChartType: ROIChartType) => {
    switch (roiChartType) {
      case ROIChartType.Reach:
        return reachQueryResult?.isLoading;
      case ROIChartType.Clicks:
        return clicksQueryResult?.isLoading;
      case ROIChartType.Engagements:
        return engagementsQueryResult?.isLoading;
    }
  };

  const getROIQueryResult = (roiChartType: ROIChartType) => {
    switch (roiChartType) {
      case ROIChartType.Reach:
        return reachQueryResult?.data?.roiReachOverviewStats;
      case ROIChartType.Clicks:
        return clicksQueryResult?.data?.roiClicksOverviewStats;
      case ROIChartType.Engagements:
        return engagementsQueryResult?.data?.roiEngagementsOverviewStats;
    }
  };

  function renderROISummaryTabs(roiChartType: ROIChartType) {
    if (!getROIQueryResult(roiChartType)) {
      return null;
    }

    const { summary } = getROIQueryResult(roiChartType);
    return (
      <Grid container alignItems="center" justify="space-between" className={classes.tabRoot}>
        <Grid item>
          <Typography
            variant="subtitle1"
            color="primary"
            style={{ fontWeight: 600, textTransform: 'capitalize' }}
          >
            {t(`analytics:roi_chart_header_${roiChartType}`)}
          </Typography>
          <Typography variant="h2">${formatStat(summary.currency)}</Typography>
          <Change countChange={summary.change} />
        </Grid>
      </Grid>
    );
  }

  function renderROIChart(roiChartType: ROIChartType) {
    if (!getROIQueryResult(roiChartType)) {
      return null;
    }

    const { data } = getROIQueryResult(roiChartType);
    const config: ILineChartConfig[] = [];
    config.push(
      {
        label: roiChartType,
        dataKey: 'currency',
      },
      {
        label: `Previous ${roiChartType}`,
        dataKey: 'previous_currency',
      }
    );
    return <ROILineChart height={CHART_HEIGHT} data={data} config={config} />;
  }

  function updateCurrencyValues(values: any) {
    setCurrencyValues({
      reach_twitter: values.reach_twitter
        ? values.reach_twitter
        : DEFAULT_CURRENCY_VALUES.reach_twitter,
      reach_linkedin: values.reach_linkedin
        ? values.reach_linkedin
        : DEFAULT_CURRENCY_VALUES.reach_linkedin,
      clicks_twitter: values.clicks_twitter
        ? values.clicks_twitter
        : DEFAULT_CURRENCY_VALUES.clicks_twitter,
      clicks_linkedin: values.clicks_linkedin
        ? values.clicks_linkedin
        : DEFAULT_CURRENCY_VALUES.clicks_linkedin,
      clicks_facebook: values.clicks_facebook
        ? values.clicks_facebook
        : DEFAULT_CURRENCY_VALUES.clicks_facebook,
      engagements_twitter: values.engagements_twitter
        ? values.engagements_twitter
        : DEFAULT_CURRENCY_VALUES.engagements_twitter,
      engagements_linkedin: values.engagements_linkedin
        ? values.engagements_linkedin
        : DEFAULT_CURRENCY_VALUES.engagements_linkedin,
      engagements_facebook: values.engagements_facebook
        ? values.engagements_facebook
        : DEFAULT_CURRENCY_VALUES.engagements_facebook,
    });
  }

  function handleSubmit(values: any) {
    updateCurrencyValues(values);
    mutate({ user_id: user.id, roi_currency_values: values });
  }

  function renderROIInput(roiChartType: ROIChartType, network: Network) {
    return (
      <Grid container spacing={1} alignItems="center" className={classes.roiInputContainer}>
        <Grid item xs={4} className={classes.roiNetworkContainer}>
          <Typography align="left" variant="h6" color="textSecondary">
            {t(`admin:${network}`)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          {!getROIQueryLoadingStatus(roiChartType) && (
            <Typography align="right" variant="subtitle1" color="textSecondary">
              {getROIQueryResult(roiChartType).summary[`${network}_count`]}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <Typography align="left" variant="subtitle1" color="textSecondary">
            x
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <ValidatedTextFieldNext
            name={`${roiChartType}_${network}`}
            type="tel"
            className={classes.roiNumberInput}
            InputProps={{
              startAdornment: (
                <InputAdornment className={classes.roiNumberInputAdornment} position="start">
                  <Typography align="right" variant="subtitle1" color="textSecondary">
                    $
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item>
          {!getROIQueryLoadingStatus(roiChartType) && (
            <Typography align="left" variant="subtitle1" color="textSecondary">
              {`= $${formatStat(getROIQueryResult(roiChartType).summary[network])}`}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  }

  function renderROINetworkInputs(roiChartType: ROIChartType) {
    return (
      <Grid
        container
        direction="column"
        alignItems="flex-start"
        spacing={2}
        className={classes.tabbedChartWrapper}
      >
        <Grid item>
          <Typography variant="h4">{t(`analytics:roi_${roiChartType}_inputs`)}</Typography>
        </Grid>
        <Grid item>{renderROIInput(roiChartType, Network.Twitter)}</Grid>
        <Grid item>{renderROIInput(roiChartType, Network.Linkedin)}</Grid>
        {roiChartType === ROIChartType.Clicks && (
          <Grid item>{renderROIInput(roiChartType, Network.Facebook)}</Grid>
        )}
      </Grid>
    );
  }

  function renderROIInputActionButtons(props: FormikProps<typeof DEFAULT_CURRENCY_VALUES>) {
    return (
      <Grid container justify="flex-end" spacing={2} alignItems="center">
        {!collapseValues && (
          <Grid item>
            <Button onClick={() => setCollapseValues(!collapseValues)}>
              {t(`analytics:roi_edit_values`)}
            </Button>
          </Grid>
        )}
        {collapseValues && (
          <Fragment>
            <Grid item>
              <Button
                type="reset"
                onClick={() => props.resetForm({ values: DEFAULT_CURRENCY_VALUES })}
              >
                {t(`analytics:roi_restore_defaults`)}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={classes.applyButton}
                variant="contained"
                size="large"
                type="submit"
              >
                {t(`common:apply`)}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                onClick={() => setCollapseValues(!collapseValues)}
              >
                {t(`common:done`)}
              </Button>
            </Grid>
          </Fragment>
        )}
      </Grid>
    );
  }

  function renderROIInputActionBar(props: FormikProps<typeof DEFAULT_CURRENCY_VALUES>) {
    return (
      <Grid container direction="column">
        <Grid item>
          <Divider />
        </Grid>
        <Grid item>
          <Grid
            container
            justify="space-between"
            alignItems="center"
            spacing={2}
            className={collapseValues ? classes.tabbedChartWrapper : ''}
          >
            <Grid item>
              <DatePickers.Range
                dateRange={dateRange}
                startDate={startDate}
                endDate={endDate}
                onSetStart={setStartDate}
                onSetEnd={setEndDate}
                onSetRange={setDateRange}
              />
            </Grid>
            <Grid item>{renderROIInputActionButtons(props)}</Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <Typography paragraph variant="h5" color="textSecondary">
        {t(`analytics:roi_header`)}
      </Typography>
      <Paper>
        <Grid container justify="space-between" alignItems="center" spacing={5}>
          {Object.keys(ROIChartType).map(roiChartType => (
            <Grid key={`roi_${roiChartType}_chart`} item xs={4}>
              {getROIQueryLoadingStatus(ROIChartType[roiChartType]) ||
              userROIValuesQueryResult.isLoading ? (
                <TabbedChartLoader type="chartLine" height={CHART_HEIGHT} />
              ) : (
                <Fragment>
                  {renderROISummaryTabs(ROIChartType[roiChartType])}
                  <div className={classes.tabbedChartWrapper}>
                    {renderROIChart(ROIChartType[roiChartType])}
                  </div>
                </Fragment>
              )}
            </Grid>
          ))}
        </Grid>
        {!userROIValuesQueryResult.isLoading && (
          <Formik
            onSubmit={values => handleSubmit(values)}
            initialValues={currencyValues}
            validationSchema={ROICurrencyInputSchema()}
          >
            {props => (
              <form onSubmit={props.handleSubmit}>
                <Grid container direction="column">
                  <Grid item xs>
                    <Collapse in={collapseValues} timeout="auto" unmountOnExit>
                      <Grid container justify="space-between" alignItems="baseline" spacing={5}>
                        {Object.keys(ROIChartType).map(roiChartType => (
                          <Grid key={`roi_${roiChartType}_inputs`} item xs={4}>
                            {renderROINetworkInputs(ROIChartType[roiChartType])}
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Grid>
                  <Grid item>{renderROIInputActionBar(props)}</Grid>
                </Grid>
              </form>
            )}
          </Formik>
        )}
      </Paper>
    </Fragment>
  );
};

export default ROIOverviewStats;
